/**
 * Every situation the team can recognize in something an owner types or says.
 * An intent is the whole decision: the router names it, `conversation.ts`
 * decides the reply. Domains are the topics the intents belong to, so a
 * question nobody planned for can still land on the right topic and get its
 * general answer instead of a flat "no sé".
 */

export const DOMAINS = [
  'finanzas', // Mercurio: the numbers of the business
  'marketing', // Venus: offers, clients who do not come back, more clients
  'operaciones', // Marte: tomorrow's visits
  'direccion', // Júpiter: what to do first, what the team can do
  'propuestas', // reactions to the work the team already prepared
  'charla', // greetings, thanks, praise, complaints
  'acciones', // orders the team cannot carry out by itself
  'fuera', // anything else
] as const;

export type DomainId = (typeof DOMAINS)[number];

const DOMAIN_OF = {
  // finanzas: the 22 situations the rules already answer, plus advice and "anything else about money".
  ventas: 'finanzas',
  hoy: 'finanzas',
  ayer: 'finanzas',
  semana: 'finanzas',
  'mes-pasado': 'finanzas',
  anio: 'finanzas',
  comparar: 'finanzas',
  resultado: 'finanzas',
  gastos: 'finanzas',
  'mayor-gasto': 'finanzas',
  planilla: 'finanzas',
  'gastos-fijos': 'finanzas',
  'por-cobrar': 'finanzas',
  metodo: 'finanzas',
  servicio: 'finanzas',
  equipo: 'finanzas',
  cliente: 'finanzas',
  ticket: 'finanzas',
  faltas: 'finanzas',
  impuesto: 'finanzas',
  resumen: 'finanzas',
  'sin-info': 'finanzas',
  mejorar: 'finanzas',
  'finanzas-otro': 'finanzas',

  oferta: 'marketing',
  winback: 'marketing',
  'mas-clientes': 'marketing',
  'marketing-otro': 'marketing',

  confirmar: 'operaciones',
  'operaciones-otro': 'operaciones',

  primero: 'direccion',
  puedo: 'direccion',
  'quien-eres': 'direccion',
  'direccion-otro': 'direccion',

  'propuesta-rechazar': 'propuestas',
  'propuesta-aprobar': 'propuestas',
  'propuesta-cambiar': 'propuestas',
  'propuesta-porque': 'propuestas',
  'propuesta-otra': 'propuestas',
  'propuesta-despues': 'propuestas',
  'propuestas-otro': 'propuestas',

  saludo: 'charla',
  gracias: 'charla',
  despedida: 'charla',
  elogio: 'charla',
  queja: 'charla',

  accion: 'acciones',

  fuera: 'fuera',
} as const satisfies Record<string, DomainId>;

export type IntentId = keyof typeof DOMAIN_OF;

export const INTENT_IDS = Object.keys(DOMAIN_OF) as readonly IntentId[];

export function isIntentId(value: unknown): value is IntentId {
  return typeof value === 'string' && Object.hasOwn(DOMAIN_OF, value);
}

export function domainOf(id: IntentId): DomainId {
  return DOMAIN_OF[id];
}

/** A fresh list each time, so callers can sort or trim it without touching the catalog. */
export function intentsOf(domain: DomainId): IntentId[] {
  return INTENT_IDS.filter((id) => DOMAIN_OF[id] === domain);
}

/**
 * What answers when the topic is clear but the exact question was not planned:
 * the "other" class of each domain, which `conversation.ts` maps to the closest
 * general reply of that topic (the month summary, what Venus has ready, and so on).
 */
export const DOMAIN_FALLBACK: Readonly<Record<DomainId, IntentId>> = {
  finanzas: 'finanzas-otro',
  marketing: 'marketing-otro',
  operaciones: 'operaciones-otro',
  direccion: 'direccion-otro',
  propuestas: 'propuestas-otro',
  charla: 'gracias',
  acciones: 'accion',
  fuera: 'fuera',
};

/**
 * A fingerprint of the classes, in order. The shipped model stores it, so a model trained for
 * other classes than the ones in the code is refused instead of answering with the wrong names.
 */
export function classKey(): number {
  const names = [...INTENT_IDS, '|', ...DOMAINS].join(',');
  let hash = 0x811c9dc5;
  for (let at = 0; at < names.length; at += 1) {
    hash ^= names.charCodeAt(at);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
